/**
 * Shared data-availability helper for Trace.
 *
 * Purpose:
 *   Classify ad-property / campaign / ad-group rows as "date-wise",
 *   "week-wise", "month-wise" or "mixed" so the Funnel Breakdown and Ad
 *   Format Breakdown pages can:
 *     1. Show a small pill in the property/campaign name cell.
 *     2. Exclude non-date-wise rows from the cumulative line chart.
 *     3. Render a dynamic caption explaining what's included / excluded.
 *
 * Same thresholds as backend `routes/availability.py` — keeping them in
 * lockstep so pill and chart agree.
 */

// -----------------------------------------------------------------------------
// Classification
// -----------------------------------------------------------------------------
export function classifyDates(dates) {
  const clean = (dates || []).filter(Boolean);
  if (clean.length === 0) return { availability_type: 'unknown', date_wise_ranges: [], distinct_dates: 0 };

  const distinct = Array.from(new Set(clean)).sort();
  const byMonth = {};
  for (const d of distinct) {
    const m = d.substring(0, 7);
    (byMonth[m] = byMonth[m] || new Set()).add(d);
  }

  const dateWiseRanges = [];
  const perMonth = {};
  for (const [m, s] of Object.entries(byMonth)) {
    if (s.size >= 2) {
      perMonth[m] = 'date-wise';
      const arr = Array.from(s).sort();
      dateWiseRanges.push({ month: m, from: arr[0], to: arr[arr.length - 1], days: arr.length });
    } else {
      perMonth[m] = 'month-wise';
    }
  }

  const values = Object.values(perMonth);
  const allDateWise = values.length > 0 && values.every(v => v === 'date-wise');
  const anyDateWise = values.some(v => v === 'date-wise');

  if (allDateWise) return { availability_type: 'date-wise', date_wise_ranges: dateWiseRanges, distinct_dates: distinct.length };
  if (anyDateWise) return { availability_type: 'mixed',     date_wise_ranges: dateWiseRanges, distinct_dates: distinct.length };

  // Every month has ≤1 distinct date. Decide week vs month by total span.
  const dMin = new Date(distinct[0]);
  const dMax = new Date(distinct[distinct.length - 1]);
  const spanDays = Math.round((dMax - dMin) / (1000 * 60 * 60 * 24)) + 1;
  if (spanDays >= 4 && spanDays <= 20 && distinct.length <= 3) {
    return { availability_type: 'week-wise', date_wise_ranges: [], distinct_dates: distinct.length, span_days: spanDays };
  }
  return { availability_type: 'month-wise', date_wise_ranges: [], distinct_dates: distinct.length, span_days: spanDays };
}

// Given a list of raw ad-property rows, group by the requested key and return
// { [key]: { availability_type, date_wise_ranges, ... } }
export function classifyRowsByKey(rows, keyFn) {
  const byKey = {};
  for (const r of rows || []) {
    const k = keyFn(r);
    if (!k) continue;
    (byKey[k] = byKey[k] || []).push(r.date);
  }
  const out = {};
  for (const [k, dates] of Object.entries(byKey)) out[k] = classifyDates(dates);
  return out;
}

// -----------------------------------------------------------------------------
// Row filter — return only rows whose parent group is date-wise (or mixed)
// -----------------------------------------------------------------------------
// For "mixed" groups we keep only the rows falling within their date-wise
// sub-ranges (per-month buckets that had ≥2 distinct dates).
export function keepDateWiseRows(rows, keyFn, classificationByKey) {
  const out = [];
  for (const r of rows || []) {
    const k = keyFn(r);
    const c = classificationByKey?.[k];
    if (!c) continue;
    const t = c.availability_type;
    if (t === 'date-wise') { out.push(r); continue; }
    if (t === 'mixed') {
      const m = String(r.date || '').substring(0, 7);
      if ((c.date_wise_ranges || []).some(x => x.month === m)) out.push(r);
    }
    // month-wise / week-wise / unknown → dropped from chart
  }
  return out;
}

// -----------------------------------------------------------------------------
// Pill + Caption UI helpers
// -----------------------------------------------------------------------------
export const PILL_STYLES = {
  'date-wise':  { bg: '#E6F7F5', fg: '#0F766E', label: 'Date-wise' },
  'week-wise':  { bg: '#FEF3C7', fg: '#92400E', label: 'Week-wise' },
  'month-wise': { bg: '#FEF3C7', fg: '#92400E', label: 'Month-wise' },
  'mixed':      { bg: '#FEF3C7', fg: '#92400E', label: 'Mixed' },
  'unknown':    { bg: '#F3F4F6', fg: '#6B7280', label: 'Unknown' },
};

export function pillTooltip(type) {
  if (type === 'date-wise') return 'This property reports on a daily basis and is included in the graph above.';
  if (type === 'mixed')     return 'This property reports date-wise in some ranges and month-wise in others. Only the date-wise portion is included in the graph above.';
  if (type === 'week-wise') return 'This property reports on a week-wise basis. Its total is shown in the table but it is excluded from the date-wise graph above.';
  if (type === 'month-wise')return 'This property reports on a month-wise basis. Its total is shown in the table but it is excluded from the date-wise graph above.';
  return 'Availability unknown.';
}

// Build a short caption of the form:
//   "Cumulative — PLA + PCA (date-wise data). Brand Ads excluded, month-wise data only."
// `entries` is an array of { name, type } where type is the availability_type.
export function buildChartCaption(entries) {
  const included = entries.filter(e => e.type === 'date-wise' || e.type === 'mixed').map(e => e.name);
  const excluded = entries.filter(e => e.type === 'month-wise' || e.type === 'week-wise').map(e => ({ name: e.name, type: e.type }));

  if (included.length === 0 && excluded.length === 0) return '';
  const incPart = included.length > 0
    ? `Cumulative — ${included.join(' + ')} (date-wise data).`
    : 'No date-wise data available at this level.';

  if (excluded.length === 0) return incPart;

  // Group excluded by their availability type for a compact sentence.
  const excNames = excluded.map(e => e.name).join(', ');
  const kind = excluded.every(e => e.type === 'month-wise') ? 'month-wise'
             : excluded.every(e => e.type === 'week-wise')  ? 'week-wise'
             : 'non-date-wise';
  return `${incPart} ${excNames} excluded, ${kind} data only.`;
}
